import { resolveStepCatalogTypes } from "../../lib/onboarding/journeyEnumCatalog";
import { toEnum, toEnumArray } from "../../lib/onboarding/onboardingVocabulary";
import {
	dependentsToClear,
	fieldChanged,
	SCREEN_FIELD,
	wasCommitted,
} from "../../machines/onboarding/cascadeInvalidation";
import {
	closeStep,
	getJourneyProgress,
	openStep,
} from "../../services/journeyProgressService";
import * as OnboardingService from "../../services/onboardingService";
import type { OnboardingAnswers } from "../../types/onboarding";

// Maps a machine screen id → the per-screen save payload + endpoint (contract §5).
// Values are converted to enums here so the API gate accepts them.
export async function saveScreen(
	screenId: string,
	answers: OnboardingAnswers,
): Promise<void> {
	switch (screenId) {
		case "setupDetails":
			await OnboardingService.saveOnboardingFields({
				gender:
					toEnum("gender", String(answers.gender ?? "")) ?? answers.gender,
				yearOfBirth: answers.yearOfBirth,
			});
			return;
		case "status": {
			const fields: OnboardingAnswers = {
				fertilityStatus:
					toEnum("fertilityStatus", String(answers.fertilityStatus ?? "")) ??
					answers.fertilityStatus,
			};
			if (answers.donorType) fields.donorType = answers.donorType;
			await OnboardingService.saveOnboardingFields(fields);
			return;
		}
		case "role":
			await OnboardingService.saveOnboardingField(
				"fertilityRole",
				toEnum("fertilityRole", String(answers.fertilityRole ?? "")) ??
					String(answers.fertilityRole ?? ""),
			);
			return;
		case "journey":
			await OnboardingService.saveOnboardingField(
				"fertilityJourneyTypes",
				toEnumArray(
					"journeyType",
					Array.isArray(answers.fertilityJourneyTypes)
						? answers.fertilityJourneyTypes
						: [],
				),
			);
			return;
		case "cycle":
			await OnboardingService.saveOnboardingFields(
				answers.cycleNotRelevant === "true"
					? // Clear any previously-saved cycle data so the persisted record
						// reflects the user's latest intent (no stale period/length).
						{
							cycleNotRelevant: "true",
							cycleLastPeriodStart: "",
							cyclePeriodLengthDays: "",
							cycleLengthDays: "",
							cycleRegular: "",
						}
					: {
							cycleNotRelevant: "false",
							cycleLastPeriodStart: answers.cycleLastPeriodStart,
							cyclePeriodLengthDays: answers.cyclePeriodLengthDays ?? "5",
							cycleLengthDays: answers.cycleLengthDays ?? "28",
							cycleRegular: answers.cycleRegular ?? "true",
						},
			);
			return;
		case "needs":
			await OnboardingService.saveOnboardingField(
				"needsMost",
				Array.isArray(answers.needsMost) ? answers.needsMost : [],
			);
			return;
		case "setupProfile": {
			const fields: OnboardingAnswers = {};
			for (const key of [
				"userType",
				"firstName",
				"lastName",
				"countryCity",
			] as const) {
				const value = answers[key];
				if (value !== undefined && value !== null && value !== "") {
					fields[key] = value;
				}
			}
			if (Object.keys(fields).length > 0) {
				await OnboardingService.saveOnboardingFields(fields);
			}
			return;
		}
		case "howDidYouHear":
			await OnboardingService.saveOnboardingField(
				"howDidYouHearAboutUs",
				Array.isArray(answers.howDidYouHearAboutUs)
					? answers.howDidYouHearAboutUs
					: [],
			);
			return;
		case "additionalInfo":
			await OnboardingService.saveOnboardingField(
				"additionalInformation",
				String(answers.additionalInformation ?? ""),
			);
			return;
		case "steps": {
			// Batch-persist the checked steps (contract §5): every checked step but
			// the latest per journey is completed (close); the latest is current (open).
			const checked = Array.isArray(answers.journeyCheckedStepIds)
				? answers.journeyCheckedStepIds
				: [];
			const byJourney = new Map<string, string[]>();
			for (const entry of checked) {
				const sep = entry.indexOf(":");
				if (sep <= 0) continue;
				const journeyType = entry.slice(0, sep);
				const stepId = entry.slice(sep + 1);
				const list = byJourney.get(journeyType) ?? [];
				list.push(stepId);
				byJourney.set(journeyType, list);
			}
			for (const [journeyType, stepIds] of byJourney) {
				for (let i = 0; i < stepIds.length - 1; i++) {
					await closeStep({ journeyType, stepId: stepIds[i] });
				}
				await openStep({ journeyType, stepId: stepIds[stepIds.length - 1] });
			}
			return;
		}
		default:
			// setupProfile/status/role/journey/cycle/needs/howDidYouHear/additionalInfo
			// and steps are handled above; nothing else needs a save on NEXT.
			return;
	}
}

// Rebuild journeyCheckedStepIds (`catalogType:stepId`) from server step-state.
// Step progress lives in UserStepState (NOT /api/user), so onboarding resume
// can't otherwise tell the steps screen was completed — it would bounce a
// returning user back to it. `saveScreen` closes earlier steps (status
// "complete") and opens the current one ("in_progress"), so both count as
// checked. Best-effort: a per-journey fetch failure yields no ids for it.
export async function reconstructCheckedStepIds(
	statusValue: string | undefined,
	roleValue: string | undefined,
	journeyTypes: string[],
): Promise<string[]> {
	const statusEnum = toEnum("fertilityStatus", String(statusValue ?? ""));
	const roleEnum = toEnum("fertilityRole", String(roleValue ?? ""));
	const journeyEnums = toEnumArray("journeyType", journeyTypes);
	const catalogTypes = resolveStepCatalogTypes(
		statusEnum,
		roleEnum,
		journeyEnums,
	);
	const ids: string[] = [];
	for (const catalogType of catalogTypes) {
		try {
			const progress = await getJourneyProgress(catalogType);
			for (const step of progress.stepStates) {
				if (step.status === "complete" || step.status === "in_progress") {
					ids.push(`${catalogType}:${step.stepId}`);
				}
			}
		} catch {
			// No progress / fetch failure → treat as nothing checked for this journey.
		}
	}
	return ids;
}

// journeyCheckedStepIds is NOT a UserDetails column (step progress lives in
// UserStepState via journeyProgressService), so it is cleared in context only.
const NON_DB_DEPENDENTS = new Set(["journeyCheckedStepIds"]);

// Null the DB columns that depend on a just-changed field — including columns for
// screens that will now be skipped (spec decision 1). Uses the dedicated
// clear-onboarding-fields endpoint (sets each column NULL, cycleNotRelevant→false);
// save-onboarding-fields cannot clear because it drops empty values.
export async function clearDependentFieldsInDb(field: string): Promise<void> {
	const cols = dependentsToClear(field).filter(
		(dep) => !NON_DB_DEPENDENTS.has(dep),
	);
	if (cols.length === 0) return;
	await OnboardingService.clearOnboardingFields(cols);
}

// Persist a screen on NEXT: save it, then (only on a real Back-edit of this
// screen's owned field) best-effort clear the DB columns that edit invalidated.
// Keeps OnboardingWizard a pure view — it awaits this and advances. A saveScreen
// failure propagates so NEXT does not fire; a clear failure is swallowed (the
// in-context cascade already fixed the UI; persisted clearing is best-effort).
export async function persistScreen(
	screenId: string,
	answers: OnboardingAnswers,
	committed: OnboardingAnswers,
): Promise<void> {
	await saveScreen(screenId, answers);
	const field = SCREEN_FIELD[screenId];
	if (
		field !== undefined &&
		wasCommitted(committed[field]) &&
		fieldChanged(committed[field], answers[field])
	) {
		try {
			await clearDependentFieldsInDb(field);
		} catch (clearError) {
			console.error("Onboarding dependent-field clear failed:", clearError);
		}
	}
}
